import { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import {
  ISOCHRONE_AGES,
  M34_AGE_GYR,
  turnoffPoint,
  type AgeIsochrone,
  type IsoPoint,
} from '../data/isochrones';
import { PARSEC_ISOCHRONE_AGES, PARSEC_SOURCE } from '../data/parsecIsochrones';
import type { Star } from '../data/types';
import { finiteValues, hrDomains } from '../utils/chartScales';

const AGE_COLORS: Record<number, string> = {
  0.08: '#6b7c9e',
  0.1: '#8a98b4',
  0.2: '#e8c547',
  0.4: '#c4a86a',
  0.6: '#a8886a',
  1.0: '#8878a8',
};

const PARSEC_COLOR = '#67c4e8';

interface Props {
  stars: Star[];
}

export function IsochroneExplorer({ stars }: Props) {
  const [selected, setSelected] = useState<number[]>([0.1, 0.2, 0.4, 0.6]);
  const [showParsec, setShowParsec] = useState(true);
  const svgRef = useRef<SVGSVGElement>(null);

  const activeIsochrones = useMemo(
    () => ISOCHRONE_AGES.filter((a) => selected.includes(a.ageGyr)),
    [selected],
  );

  const activeParsec = useMemo(
    () => (showParsec ? PARSEC_ISOCHRONE_AGES.filter((a) => selected.includes(a.ageGyr)) : []),
    [selected, showParsec],
  );

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    if (!svg.node()) return;
    svg.selectAll('*').remove();

    const width = svgRef.current!.clientWidth || 640;
    const height = 380;
    const margin = { top: 28, right: 24, bottom: 48, left: 56 };

    svg.attr('viewBox', `0 0 ${width} ${height}`);

    const isoPoints = [
      ...activeIsochrones.flatMap((a) => a.points),
      ...activeParsec.flatMap((a) => a.points),
    ];
    const { xDomain, yDomain } = hrDomains(
      [...finiteValues(stars.map((s) => s.bv)), ...finiteValues(isoPoints.map((p) => p.bv))],
      [...finiteValues(stars.map((s) => s.mv)), ...finiteValues(isoPoints.map((p) => p.mv))],
    );

    const x = d3.scaleLinear().domain(xDomain).nice().range([margin.left, width - margin.right]);
    const y = d3.scaleLinear().domain(yDomain).nice().range([height - margin.bottom, margin.top]);

    const g = svg.append('g');

    g.append('g')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x).ticks(7))
      .call((sel) => sel.select('.domain').remove())
      .call((sel) => sel.selectAll('text').attr('fill', '#8a98b4').attr('font-size', 10));

    g.append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(7))
      .call((sel) => sel.select('.domain').remove())
      .call((sel) => sel.selectAll('text').attr('fill', '#8a98b4').attr('font-size', 10));

    g.append('text')
      .attr('x', width / 2)
      .attr('y', height - 10)
      .attr('text-anchor', 'middle')
      .attr('fill', '#9aa8c4')
      .attr('font-size', 10)
      .text('B − V');

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -(height / 2))
      .attr('y', 14)
      .attr('text-anchor', 'middle')
      .attr('fill', '#9aa8c4')
      .attr('font-size', 10)
      .text('Mv');

    const line = d3
      .line<IsoPoint>()
      .x((d) => x(d.bv))
      .y((d) => y(d.mv));

    activeIsochrones.forEach((iso) => {
      const color = AGE_COLORS[iso.ageGyr] ?? '#888';
      const isBest = iso.ageGyr === M34_AGE_GYR;
      g.append('path')
        .datum(iso.points)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', isBest ? 2.8 : 1.6)
        .attr('stroke-dasharray', isBest ? null : '5 4')
        .attr('opacity', isBest ? 1 : 0.8)
        .attr('d', line);
    });

    activeParsec.forEach((iso) => {
      const isBest = iso.ageGyr === M34_AGE_GYR;
      g.append('path')
        .datum(iso.points)
        .attr('fill', 'none')
        .attr('stroke', PARSEC_COLOR)
        .attr('stroke-width', isBest ? 2.2 : 1.4)
        .attr('stroke-dasharray', '3 3')
        .attr('opacity', isBest ? 0.95 : 0.65)
        .attr('d', line);
    });

    activeIsochrones.forEach((iso) => {
      const color = AGE_COLORS[iso.ageGyr] ?? '#888';
      const isBest = iso.ageGyr === M34_AGE_GYR;
      const to = turnoffPoint(iso.points);
      g.append('circle')
        .attr('cx', x(to.bv))
        .attr('cy', y(to.mv))
        .attr('r', isBest ? 4.5 : 3)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 1.2);
    });

    g.selectAll('circle.star')
      .data(stars)
      .join('circle')
      .attr('class', 'star')
      .attr('cx', (d) => x(d.bv))
      .attr('cy', (d) => y(d.mv))
      .attr('r', 2.2)
      .attr('fill', '#eef2ff')
      .attr('opacity', 0.35);

    const legendX = width - margin.right;
    let legendY = margin.top;
    activeIsochrones.forEach((iso) => {
      const color = AGE_COLORS[iso.ageGyr] ?? '#888';
      g.append('line')
        .attr('x1', legendX - 48)
        .attr('x2', legendX - 28)
        .attr('y1', legendY)
        .attr('y2', legendY)
        .attr('stroke', color)
        .attr('stroke-width', iso.ageGyr === M34_AGE_GYR ? 2.5 : 1.5);
      g.append('text')
        .attr('x', legendX - 24)
        .attr('y', legendY + 3)
        .attr('fill', '#9aa8c4')
        .attr('font-size', 9)
        .text(iso.shortLabel);
      legendY += 14;
    });

    if (showParsec) {
      g.append('line')
        .attr('x1', legendX - 48)
        .attr('x2', legendX - 28)
        .attr('y1', legendY)
        .attr('y2', legendY)
        .attr('stroke', PARSEC_COLOR)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '3 3');
      g.append('text')
        .attr('x', legendX - 24)
        .attr('y', legendY + 3)
        .attr('fill', '#9aa8c4')
        .attr('font-size', 9)
        .text('PARSEC');
    }
  }, [stars, activeIsochrones, activeParsec, showParsec]);

  const toggleAge = (ageGyr: number) => {
    setSelected((prev) => {
      if (prev.includes(ageGyr)) {
        return prev.length > 1 ? prev.filter((a) => a !== ageGyr) : prev;
      }
      return [...prev, ageGyr].sort((a, b) => a - b);
    });
  };

  return (
    <div className="iso-explorer">
      <div className="iso-explorer__header">
        <h3 className="iso-explorer__title">Isochrone gallery</h3>
        <p className="iso-explorer__intro">
          Solid curves: legacy Yonsei–Yale tracks from Midas ISO.csv. Dashed cyan: PARSEC v1.2S (
          {PARSEC_SOURCE}). At 200 Myr the turnoff differs by ~0.5 mag — worth checking when tuning
          binary cuts. M34 best fit: <strong>YY gold</strong> vs <strong>PARSEC cyan</strong>.
        </p>
      </div>

      <label className="hr-filters__item iso-explorer__parsec-toggle">
        <input
          type="checkbox"
          checked={showParsec}
          onChange={(e) => setShowParsec(e.target.checked)}
        />
        Overlay PARSEC v1.2S (dashed)
      </label>

      <div className="iso-explorer__chips" role="group" aria-label="Select isochrone ages">
        {ISOCHRONE_AGES.map((iso) => {
          const on = selected.includes(iso.ageGyr);
          return (
            <button
              key={iso.ageGyr}
              type="button"
              className={`iso-explorer__chip${on ? ' is-active' : ''}${iso.ageGyr === M34_AGE_GYR ? ' is-m34' : ''}`}
              aria-pressed={on}
              onClick={() => toggleAge(iso.ageGyr)}
              style={{ '--chip-color': AGE_COLORS[iso.ageGyr] } as React.CSSProperties}
            >
              {iso.label}
            </button>
          );
        })}
      </div>

      <div className="iso-explorer__chart">
        <svg ref={svgRef} className="chart-svg" role="img" aria-label="Interactive isochrone comparison" />
      </div>

      <div className="iso-explorer__cards">
        {activeIsochrones.map((iso) => (
          <IsoCard
            key={iso.ageGyr}
            iso={iso}
            isBest={iso.ageGyr === M34_AGE_GYR}
            parsec={
              showParsec ? PARSEC_ISOCHRONE_AGES.find((p) => p.ageGyr === iso.ageGyr) : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}

function IsoCard({
  iso,
  isBest,
  parsec,
}: {
  iso: AgeIsochrone;
  isBest: boolean;
  parsec?: AgeIsochrone;
}) {
  const to = turnoffPoint(iso.points);
  const pto = parsec ? turnoffPoint(parsec.points) : null;
  return (
    <article className={`iso-explorer__card${isBest ? ' iso-explorer__card--best' : ''}`}>
      <header>
        <span className="iso-explorer__card-age" style={{ color: AGE_COLORS[iso.ageGyr] }}>
          {iso.label}
        </span>
        {isBest && <span className="iso-explorer__badge">M34 fit</span>}
      </header>
      <p>{iso.note}</p>
      <dl className="iso-explorer__stats">
        <div>
          <dt>YY Mv</dt>
          <dd>{to.mv.toFixed(2)}</dd>
        </div>
        <div>
          <dt>YY B−V</dt>
          <dd>{to.bv.toFixed(2)}</dd>
        </div>
        {pto && (
          <>
            <div>
              <dt>PARSEC Mv</dt>
              <dd style={{ color: PARSEC_COLOR }}>{pto.mv.toFixed(2)}</dd>
            </div>
            <div>
              <dt>PARSEC B−V</dt>
              <dd style={{ color: PARSEC_COLOR }}>{pto.bv.toFixed(2)}</dd>
            </div>
          </>
        )}
      </dl>
    </article>
  );
}
